import colorPallete, { bodyFonts, elevation, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DealerStateContext } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useContext } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({});

const enum fields {
    rowBackgroundColor = "rowBackgroundColor",
    innerBackgroundColor =  "innerBackgroundColor",
    titleAlignment = "titleAlignment",
    eventTitle = "eventTitle",
    eventWithPromotionTitle = "eventWithPromotionTitle",
    promotionTitle = "promotionTitle",
    branchEvent1Content = "branchEvent1Content",
    branchEvent1StartDate = "branchEvent1StartDate",
    branchEvent1EndDate = "branchEvent1EndDate",
    branchEvent2Content = "branchEvent2Content",
    branchEvent2StartDate = "branchEvent2StartDate",
    branchEvent2EndDate = "branchEvent2EndDate",
    branchEvent3Content = "branchEvent3Content",
    branchEvent3StartDate = "branchEvent3StartDate",
    branchEvent3EndDate = "branchEvent3EndDate",
    branchPromotionsContent = "branchPromotionsContent",
    branchPromotionsStartDate = "branchPromotionsStartDate",
    branchPromotionsEndDate = "branchPromotionsEndDate",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.rowBackgroundColor]: string;
        [fields.innerBackgroundColor]: string;
        [fields.titleAlignment]: "left" | "center" | "right";
        [fields.eventTitle]: string;
        [fields.eventWithPromotionTitle]: string;
        [fields.promotionTitle]: string;
        [fields.branchEvent1Content]: string;
        [fields.branchEvent1StartDate]: string;
        [fields.branchEvent1EndDate]: string;
        [fields.branchEvent2Content]: string;
        [fields.branchEvent2StartDate]: string;
        [fields.branchEvent2EndDate]: string;
        [fields.branchEvent3Content]: string;
        [fields.branchEvent3StartDate]: string;
        [fields.branchEvent3EndDate]: string;
        [fields.branchPromotionsContent]: string;
        [fields.branchPromotionsStartDate]: string;
        [fields.branchPromotionsEndDate]: string;
    };
}

type Props = OwnProps;

export interface DsgBranchEventsAndPromotionsStyles {
    wrapper?: InjectableCss;
    eventInnerWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    innerWrapper?: InjectableCss;
    eventPromotionWrapper?: InjectableCss;
    promotionWrapper?: InjectableCss;
}

export const dsgBranchEventsAndPromotionsStyles: DsgBranchEventsAndPromotionsStyles = {
    wrapper: {
        css: css`
            padding: 24px;
            margin-top: 12px; 
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        margin: 0 -16px;
                    `,
                ],
                "max",
            )}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin: 12px -16px 0;
                    `,
                ],
                "minMax",
            )}
            
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    null,
                    css`
                        padding: 24px 80px;
                    `,
                ],
                "min",
            )}
        `
    },
    titleText: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        margin-bottom: 8px;
                    `,
                ],
                "min",
            )}
        `
    },
    eventInnerWrapper: {
        css: css`
            margin: auto;
            border-radius: 8px;
            padding: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        padding: 24px 64px;
                    `,
                ],
                "min",
            )}
        `
    },
    eventPromotionWrapper: {
        css: css`
            border-bottom: 1px solid ${colorPallete.grey300};
            padding: 16px 0;
            p {
                font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
                color: ${colorPallete.grey800};
            }
            a {
                font: ${bodyFonts.body2SemiBoldLg.fontWeight} ${bodyFonts.body2SemiBoldLg.fontSize}/${bodyFonts.body2SemiBoldLg.lineHeight} ${bodyFonts.body2SemiBoldLg.fontFamily};
                [class*="StyledTypography"] {
                    display: inline-flex;
                    column-gap: 8px;
                    justify-content: center;
                }
                &,
                &:hover {
                    [class*="StyledTypography"] {
                        color: inherit;
                    }
                }
            }
            p,
            h3,
            h4,
            h5 {
                margin-bottom: 0;
            }
            .imageTextWrapper {
                display: flex;
                gap: 16px;
                flex-direction: column;
            }
            .imageWrapper {
                width: 100%;
                display: flex;
                justify-content: center;
            }
            .textWrapper {
                display: flex;
                flex-direction: column;
                row-gap: 8px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        .imageTextWrapper {
                            display: flex;
                            gap: 24px;
                            align-items: center;
                            flex-direction: row;
                        }
                        .imageWrapper {
                            min-width: 240px;
                            max-width: 240px;
                        }
                        .textWrapper {
                            width: calc(100% - 240px);
                        }
                    `,
                    css`
                        padding: 32px 16px 16px;
                        border-radius: 8px;
                    `,
                ],
                "min",
            )}
            @media print {
                .imageTextWrapper {
                    display: flex;
                    gap: 16px;
                    flex-direction: row;
                }
                .imageWrapper {
                    min-width: 240px;
                    max-width: 240px;
                }
                .textWrapper {
                    width: calc(100% - 240px);
                }
            }
        `
    }
};

const styles = dsgBranchEventsAndPromotionsStyles;

const DsgBranchEventsAndPromotionsDisplay = ({ fields }: Props) => {
    const { value: dealer } = useContext(DealerStateContext);
    if (!dealer) return null;

    const isCurrentDateInRange = (startDate?: Date, endDate?: Date): boolean => {
        if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return false;
        }
        const currentDate = new Date();
        return currentDate >= startDate && currentDate <= endDate;
    };

    const getDealerEventContent = (startKey: string, endKey: string, contentKey: string): string | null => {
        const start = new Date(dealer?.properties[startKey]);
        const end = new Date(dealer?.properties[endKey]);
        const content = dealer?.properties[contentKey];
        return content && isCurrentDateInRange(start, end) ? content : null;
    };

    const getGlobalEventContent = (startDate: Date, endDate: Date, content: string): string | null => {
        return content && isCurrentDateInRange(startDate, endDate) ? content : null;
    };

    // Priority: Dealer Events First
    const eventContents: string[] = [];

    const dealerEvent1 = getDealerEventContent("branchEventEditorStartDate1", "branchEventEditorDate1", "branchEventEditor1");
    const dealerEvent2 = getDealerEventContent("branchEventEditorStartDate2", "branchEventEditorDate2", "branchEventEditor2");
    const dealerEvent3 = getDealerEventContent("branchEventEditorStartDate3", "branchEventEditorDate3", "branchEventEditor3");

    if (dealerEvent1) eventContents.push(dealerEvent1);
    if (dealerEvent2) eventContents.push(dealerEvent2);
    if (dealerEvent3) eventContents.push(dealerEvent3);

    // Fallback to Global Events if needed
    if (eventContents.length < 3) {
        const globalEvent1 = getGlobalEventContent(new Date(fields.branchEvent1StartDate), new Date(fields.branchEvent1EndDate), fields.branchEvent1Content);
        const globalEvent2 = getGlobalEventContent(new Date(fields.branchEvent2StartDate), new Date(fields.branchEvent2EndDate), fields.branchEvent2Content);
        const globalEvent3 = getGlobalEventContent(new Date(fields.branchEvent3StartDate), new Date(fields.branchEvent3EndDate), fields.branchEvent3Content);

        const globalEvents = [globalEvent1, globalEvent2, globalEvent3].filter(Boolean) as string[];

        for (const content of globalEvents) {
            if (eventContents.length >= 3) break;
            eventContents.push(content);
        }
    }

    // Promotion logic (dealer first, fallback to global)
    const promotionContent = getDealerEventContent(
        "branchPromotionEditorStartDate",
        "branchPromotionEditorDate",
        "branchPromotionEditor"
    ) || getGlobalEventContent(
        new Date(fields.branchPromotionsStartDate),
        new Date(fields.branchPromotionsEndDate),
        fields.branchPromotionsContent
    );

    const hasPromotion = !!promotionContent;
    const hasAnyEvent = eventContents.length > 0;

    if (!hasAnyEvent && !hasPromotion) return null;

    const title = hasAnyEvent && hasPromotion
        ? fields.eventWithPromotionTitle
        : hasAnyEvent
        ? fields.eventTitle
        : hasPromotion
        ? fields.eventWithPromotionTitle
        : "";

    return (
        <StyledWrapper {...styles.wrapper} className="branchEventAndPromotionWrapper"
            style={{
                background: fields.rowBackgroundColor,
                boxShadow: `-86em 0 0 ${fields.rowBackgroundColor}, 86em 0 0 ${fields.rowBackgroundColor}`,
            }}
        >
            <StyledWrapper {...styles.eventInnerWrapper}
                style={{ background: fields.innerBackgroundColor }}>
                {title && (
                    <Typography {...styles.titleText} as="h2" style={{ textAlign: fields.titleAlignment || "left" }}>
                        {title}
                    </Typography>
                )}
                <StyledWrapper {...styles.innerWrapper} className="innerWrapper">
                    {eventContents.map((content, idx) => (
                        <StyledWrapper key={`event-${idx}`} {...styles.eventPromotionWrapper} className="eventWrapper">
                            {parse(content, parserOptions)}
                        </StyledWrapper>
                    ))}
                    {hasPromotion && (
                        <StyledWrapper {...styles.eventPromotionWrapper} className="promotionWrapper">
                            {parse(promotionContent, parserOptions)}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};



const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(DsgBranchEventsAndPromotionsDisplay),
    definition: {
        group: "Common",
        displayName: "Branch Events and Promotions Display",
        fieldDefinitions: [
            {
                name: fields.rowBackgroundColor,
                defaultValue: "#C91E24",
                displayName: "Row Background Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.innerBackgroundColor,
                defaultValue: "#FFFFFF",
                displayName: "Inner Background Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.eventTitle,
                displayName: "Event Title",
                editorTemplate: "TextField",
                defaultValue: "Upcoming Events",
                fieldType: "Contextual",
                sortOrder: 2,
            },
            {
                name: fields.eventWithPromotionTitle,
                displayName: "Event & Promotion Title",
                editorTemplate: "TextField",
                defaultValue: "Upcoming Events & Branch Promotions",
                fieldType: "Contextual",
                sortOrder: 3,
            },
            {
                name: fields.promotionTitle,
                displayName: "Promotion Title",
                editorTemplate: "TextField",
                defaultValue: "Upcoming Branch Promotions",
                fieldType: "Contextual",
                sortOrder: 4,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "center",
                horizontal: true,
                fieldType: "General",
                sortOrder: 5,
                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
            },
            {
                name: fields.branchEvent1StartDate,
                defaultValue: null,
                displayName: "Branch Event 1 Start Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.branchEvent1EndDate,
                defaultValue: null,
                displayName: "Branch Event 1 End Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.branchEvent1Content,
                displayName: "Branch Event 1 Content",
                editorTemplate: "RichTextField",
                defaultValue: `<div class="imageTextWrapper">
                <div class="imageWrapper">
                    <img src="/userfiles/event1.png">
                </div>
                <div class="textWrapper">
                    <h4 style="color: #C91E24">Ronald McDonald Fundraiser</h4>
                    <p><strong>mm/dd/yy</strong></p>
                    <p>Short description here Short description here Short description here Short description here Short description here Short description here Short description here Short description here Short description here.</p>
                    <a href="#" style="color:#C91E24;"><img src="/userfiles/download-icon.png"><strong>Event flyer</strong></a>
                </div>
            </div>`,
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 9,
            },
            {
                name: fields.branchEvent2StartDate,
                defaultValue: null,
                displayName: "Branch Event 2 Start Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.branchEvent2EndDate,
                defaultValue: null,
                displayName: "Branch Event 2 End Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.branchEvent2Content,
                displayName: "Branch Event 2 Content",
                editorTemplate: "RichTextField",
                defaultValue: `<div class="imageTextWrapper">
                    <div class="imageWrapper">
                        <img src="/userfiles/event2.png">
                    </div>
                    <div class="textWrapper">
                    <p><strong>4/4/25 from 10 am - 2 pm ONLY!</strong></p>
                        <p>Join DSG for a Counter Day in Aberdeen featuring Milwaukee Tools.<br><br>
                        10% off Milwaukee Tool Purchases during the event. Milwaukee Tool Reps will be available to answer questions and the Milwaukee Tool Trailer will be onsite with new products.
                        </p>
                    </div>
                </div>`,
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 12,
            },
            {
                name: fields.branchEvent3StartDate,
                defaultValue: null,
                displayName: "Branch Event 3 Start Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.branchEvent3EndDate,
                defaultValue: null,
                displayName: "Branch Event 3 End Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.branchEvent3Content,
                displayName: "Branch Event 3 Content",
                editorTemplate: "RichTextField",
                defaultValue: `<div class="imageTextWrapper">
                    <div class="imageWrapper">
                        <img src="/userfiles/event1.png">
                    </div>
                    <div class="textWrapper">
                        <h4 style="color: #C91E24">Ronald McDonald Fundraiser</h4>
                        <p><strong>mm/dd/yy</strong></p>
                        <p>Short description here Short description here Short description here Short description here Short description here Short description here Short description here Short description here Short description here.</p>
                        <a href="" style="color:#C91E24;"><img src="/userfiles/download-icon.png"><strong>Event flyer</strong></a>
                    </div>
                </div>`,
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 15,
            },
            {
                name: fields.branchPromotionsStartDate,
                defaultValue: null,
                displayName: "Branch Promotions Start Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 16,
            },
            {
                name: fields.branchPromotionsEndDate,
                defaultValue: null,
                displayName: "Branch Promotions End Date",
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 17,
            },
            {
                name: fields.branchPromotionsContent,
                displayName: "Branch Promotion Content",
                editorTemplate: "RichTextField",
                defaultValue: `
                <div class="imageTextWrapper">
                <div class="imageWrapper">
                    <img src="/userfiles/event2.png">
                </div>
                <div class="textWrapper">
                    <h4 style="color: #C91E24">HVAC & Fujitsu Beat the Heat Promotion</h4>
                    <p><strong>mm/dd/yy - mm/dd/yy</strong></p>
                    <p>Short description here Short description here Short description here Short description here Short description here Short description here Short description here Short description here Short description here.</p>
                    <a href="" style="color:#C91E24;"><strong>Register today</strong></a>
                </div>
            </div>
                `,
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 18,
            },
        ],
    },
};
export default widgetModule;
